import React, { useEffect, useState } from 'react';
import {
    Mail, Calendar, MapPin,
    Pencil, Check, Loader2, Monitor, KeyRound, Package, LifeBuoy
} from 'lucide-react';
import { authService } from '../../services/authService';
import { userService, User as UserType } from '../../services/userService';
import { useToast } from '../../context/ToastContext';
import { PageHeader } from '../../components/shared/PageHeader';
import {
    Button,
    Card, CardContent, CardHeader, CardTitle,
    Input,
    Avatar, AvatarFallback,
    Badge,
    Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
    Tabs, TabsContent, TabsList, TabsTrigger,
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter
} from '../../components/ui';
import { FormField } from '../../components/shared/FormField';
import { useForm } from '../../hooks/useForm';

const UserProfile: React.FC = () => {
    const [profile, setProfile] = useState<UserType | null>(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [editOpen, setEditOpen] = useState(false);
    const [inventory, setInventory] = useState<UserType | null>(null);
    const [loadingInventory, setLoadingInventory] = useState(false);
    const { showToast } = useToast();

    const {
        values: formData,
        errors,
        handleChange,
        handleBlur,
        validateForm,
        setValues: setFormData
    } = useForm({
        firstName: '',
        lastName: '',
        email: '',
        location: '',
        phoneNumber: ''
    }, {
        firstName: { required: true },
        lastName: { required: true },
        email: { required: true, email: true },
        phoneNumber: { phone: true }
    });

    useEffect(() => {
        loadProfile();
    }, []);

    const loadProfile = async () => {
        try {
            setLoading(true);
            const data = await authService.getProfile();
            setProfile(data);
            setFormData({
                firstName: data.firstName || '',
                lastName: data.lastName || '',
                email: data.email || '',
                location: data.location || '',
                phoneNumber: data.phoneNumber || ''
            });
            loadInventory();
        } catch (error) {
            showToast('Failed to load profile', 'error');
        } finally {
            setLoading(false);
        }
    };

    const loadInventory = async () => {
        try {
            setLoadingInventory(true);
            // Self-service endpoint — works for every role, including Standard User.
            const data = await userService.getMyInventory();
            setInventory(data);
        } catch (error) {
            console.error('Failed to load user inventory', error);
        } finally {
            setLoadingInventory(false);
        }
    };

    const handleUpdateProfile = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!validateForm()) return;
        try {
            setSaving(true);
            await authService.updateProfile(formData);
            showToast('Profile updated successfully', 'success');
            setEditOpen(false);
            loadProfile();
        } catch (error: any) {
            showToast(error.message || 'Failed to update profile', 'error');
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <div className="flex h-[400px] items-center justify-center">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
        );
    }

    const assetCount = inventory?.assignedAssets?.length || 0;
    const licenseCount = inventory?.licenseAssignments?.length || 0;
    const inventoryCount = inventory?.inventoryAssignments?.length || 0;

    return (
        <div className="space-y-6">
            <PageHeader
                title="My Portfolio"
                description="Manage your personal information and assigned IT resources"
            />

            {/* Profile Header */}
            <Card>
                <CardContent className="flex flex-col sm:flex-row items-start sm:items-center gap-6 p-6">
                    <Avatar className="h-20 w-20">
                        <AvatarFallback className="text-2xl bg-primary/10 text-primary">
                            {profile?.firstName?.[0]}{profile?.lastName?.[0]}
                        </AvatarFallback>
                    </Avatar>
                    <div className="flex-1 space-y-1.5">
                        <h3 className="text-xl font-bold">{profile?.firstName} {profile?.lastName}</h3>
                        <p className="text-sm text-muted-foreground">{profile?.role?.name || 'Administrator'}</p>
                        <div className="flex flex-wrap gap-2">
                            <Badge variant="success" className="gap-1.5">
                                <Check className="h-3 w-3" />
                                Active Account
                            </Badge>
                            <Badge variant="outline" className="gap-1.5">
                                <Calendar className="h-3 w-3" />
                                Joined {profile?.lastLogin ? new Date(profile.lastLogin).toLocaleDateString() : 'Recently'}
                            </Badge>
                        </div>
                        <div className="flex flex-wrap gap-x-4 gap-y-1 pt-1 text-sm text-muted-foreground">
                            {profile?.location && (
                                <span className="flex items-center gap-1.5">
                                    <MapPin className="h-3.5 w-3.5" />
                                    {profile.location}
                                </span>
                            )}
                            <span className="flex items-center gap-1.5">
                                <Mail className="h-3.5 w-3.5" />
                                {profile?.email}
                            </span>
                        </div>
                    </div>
                    <Button variant="outline" onClick={() => setEditOpen(true)}>
                        <Pencil className="h-4 w-4 mr-2" />
                        Edit Profile
                    </Button>
                </CardContent>
            </Card>

            {/* Stat Cards */}
            <div className="grid gap-4 sm:grid-cols-3">
                <Card>
                    <CardContent className="flex items-center gap-4 p-6">
                        <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-primary/10">
                            <Monitor className="h-5 w-5 text-primary" />
                        </div>
                        <div>
                            <p className="text-sm font-medium">Assigned Assets</p>
                            <p className="text-2xl font-bold text-primary">{assetCount}</p>
                            <p className="text-xs text-muted-foreground">Currently assigned</p>
                        </div>
                    </CardContent>
                </Card>
                <Card>
                    <CardContent className="flex items-center gap-4 p-6">
                        <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-success/10">
                            <KeyRound className="h-5 w-5 text-success" />
                        </div>
                        <div>
                            <p className="text-sm font-medium">Software Licenses</p>
                            <p className="text-2xl font-bold text-success">{licenseCount}</p>
                            <p className="text-xs text-muted-foreground">{licenseCount === 1 ? '1 active' : `${licenseCount} active`}</p>
                        </div>
                    </CardContent>
                </Card>
                <Card>
                    <CardContent className="flex items-center gap-4 p-6">
                        <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-warning/10">
                            <Package className="h-5 w-5 text-warning" />
                        </div>
                        <div>
                            <p className="text-sm font-medium">Inventory Items</p>
                            <p className="text-2xl font-bold text-warning">{inventoryCount}</p>
                            <p className="text-xs text-muted-foreground">In your custody</p>
                        </div>
                    </CardContent>
                </Card>
            </div>

            {/* `[&>*]:min-w-0` — grid items must be allowed to shrink, otherwise
                the tables inside widen the page instead of scrolling. */}
            <div className="grid gap-6 lg:grid-cols-3 [&>*]:min-w-0">
                {/* Tabbed resource tables */}
                <Card className="lg:col-span-2">
                    <CardContent className="p-4 sm:p-6">
                        <Tabs defaultValue="assets">
                            <TabsList className="flex h-auto w-full flex-wrap justify-start">
                                <TabsTrigger value="assets">Assets ({assetCount})</TabsTrigger>
                                <TabsTrigger value="licenses">Licenses ({licenseCount})</TabsTrigger>
                                <TabsTrigger value="inventory">Inventory ({inventoryCount})</TabsTrigger>
                            </TabsList>

                            <TabsContent value="assets">
                                {loadingInventory ? (
                                    <div className="flex h-[200px] items-center justify-center">
                                        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                                    </div>
                                ) : (
                                    <Table>
                                        <TableHeader>
                                            <TableRow>
                                                <TableHead>Asset Tag</TableHead>
                                                <TableHead>Asset</TableHead>
                                                <TableHead>Category</TableHead>
                                                <TableHead>Serial Number</TableHead>
                                                <TableHead>Assigned Date</TableHead>
                                                <TableHead>Status</TableHead>
                                            </TableRow>
                                        </TableHeader>
                                        <TableBody>
                                            {(inventory?.assignedAssets || []).length > 0 ? (
                                                inventory?.assignedAssets?.map((asset: any) => (
                                                    <TableRow key={asset.id}>
                                                        <TableCell className="font-mono font-bold text-primary">{asset.assetTag}</TableCell>
                                                        <TableCell className="font-medium">{asset.model || asset.name}</TableCell>
                                                        <TableCell>
                                                            <Badge variant="outline">{asset.category}</Badge>
                                                        </TableCell>
                                                        <TableCell className="text-sm text-muted-foreground">{asset.serialNumber || '-'}</TableCell>
                                                        <TableCell className="text-sm">
                                                            {asset.deploymentDate ? new Date(asset.deploymentDate).toLocaleDateString() : 'N/A'}
                                                        </TableCell>
                                                        <TableCell>
                                                            <Badge variant="success">Active</Badge>
                                                        </TableCell>
                                                    </TableRow>
                                                ))
                                            ) : (
                                                <TableRow>
                                                    <TableCell colSpan={6} className="text-center py-12 text-muted-foreground">
                                                        No assets are currently assigned to you.
                                                    </TableCell>
                                                </TableRow>
                                            )}
                                        </TableBody>
                                    </Table>
                                )}
                            </TabsContent>

                            <TabsContent value="licenses">
                                {loadingInventory ? (
                                    <div className="flex h-[160px] items-center justify-center">
                                        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                                    </div>
                                ) : (
                                    <Table>
                                        <TableHeader>
                                            <TableRow>
                                                <TableHead>Software</TableHead>
                                                <TableHead>Product Key</TableHead>
                                                <TableHead>Expiry Date</TableHead>
                                                <TableHead>Assigned Date</TableHead>
                                            </TableRow>
                                        </TableHeader>
                                        <TableBody>
                                            {(inventory?.licenseAssignments || []).length > 0 ? (
                                                inventory?.licenseAssignments?.map((la: any) => (
                                                    <TableRow key={la.id}>
                                                        <TableCell className="font-medium">
                                                            {la.license?.softwareName || la.license?.planName || '-'}
                                                        </TableCell>
                                                        <TableCell className="font-mono text-sm text-muted-foreground">
                                                            {la.license?.productKey || '-'}
                                                        </TableCell>
                                                        <TableCell className="text-sm">
                                                            {la.license?.expiryDate ? new Date(la.license.expiryDate).toLocaleDateString() : 'N/A'}
                                                        </TableCell>
                                                        <TableCell className="text-sm">
                                                            {la.assignedAt ? new Date(la.assignedAt).toLocaleDateString() : 'N/A'}
                                                        </TableCell>
                                                    </TableRow>
                                                ))
                                            ) : (
                                                <TableRow>
                                                    <TableCell colSpan={4} className="text-center py-12 text-muted-foreground">
                                                        No licenses are currently assigned to you.
                                                    </TableCell>
                                                </TableRow>
                                            )}
                                        </TableBody>
                                    </Table>
                                )}
                            </TabsContent>

                            <TabsContent value="inventory">
                                {loadingInventory ? (
                                    <div className="flex h-[160px] items-center justify-center">
                                        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                                    </div>
                                ) : (
                                    <Table>
                                        <TableHeader>
                                            <TableRow>
                                                <TableHead>Item</TableHead>
                                                <TableHead>Quantity</TableHead>
                                                <TableHead>Assigned Date</TableHead>
                                                <TableHead>Expected Return</TableHead>
                                            </TableRow>
                                        </TableHeader>
                                        <TableBody>
                                            {(inventory?.inventoryAssignments || []).length > 0 ? (
                                                inventory?.inventoryAssignments?.map((ia: any) => (
                                                    <TableRow key={ia.id}>
                                                        <TableCell className="font-medium">
                                                            {ia.item?.name || ia.item?.itemName || '-'}
                                                        </TableCell>
                                                        <TableCell className="text-sm">{ia.quantity}</TableCell>
                                                        <TableCell className="text-sm">
                                                            {ia.assignmentDate ? new Date(ia.assignmentDate).toLocaleDateString() : 'N/A'}
                                                        </TableCell>
                                                        <TableCell className="text-sm">
                                                            {ia.expectedReturnDate ? new Date(ia.expectedReturnDate).toLocaleDateString() : '-'}
                                                        </TableCell>
                                                    </TableRow>
                                                ))
                                            ) : (
                                                <TableRow>
                                                    <TableCell colSpan={4} className="text-center py-12 text-muted-foreground">
                                                        No inventory items are currently assigned to you.
                                                    </TableCell>
                                                </TableRow>
                                            )}
                                        </TableBody>
                                    </Table>
                                )}
                            </TabsContent>
                        </Tabs>
                    </CardContent>
                </Card>

                {/* Need Help */}
                <Card className="bg-gradient-to-br from-primary/5 to-background overflow-hidden">
                    <CardContent className="p-6 space-y-4">
                        <h4 className="font-bold flex items-center gap-2">
                            <LifeBuoy className="h-4 w-4 text-primary" />
                            Need help?
                        </h4>
                        <p className="text-sm text-muted-foreground">
                            If you're having issues with your assigned equipment or need to request new software, contact IT Support.
                        </p>
                        <Button className="w-full">
                            Contact IT Support
                        </Button>
                    </CardContent>
                </Card>
            </div>

            {/* Edit Profile Dialog */}
            <Dialog open={editOpen} onOpenChange={setEditOpen}>
                <DialogContent className="sm:max-w-lg">
                    <DialogHeader>
                        <DialogTitle>Edit Profile</DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleUpdateProfile} autoComplete="off" className="space-y-4">
                        <div className="grid gap-4 sm:grid-cols-2">
                            <FormField
                                id="firstName"
                                label="First Name"
                                required
                                error={errors.firstName}
                            >
                                <Input
                                    id="firstName"
                                    value={formData.firstName}
                                    onChange={(e) => handleChange('firstName', e.target.value)}
                                    onBlur={() => handleBlur('firstName')}
                                    placeholder="John"
                                />
                            </FormField>
                            <FormField
                                id="lastName"
                                label="Last Name"
                                required
                                error={errors.lastName}
                            >
                                <Input
                                    id="lastName"
                                    value={formData.lastName}
                                    onChange={(e) => handleChange('lastName', e.target.value)}
                                    onBlur={() => handleBlur('lastName')}
                                    placeholder="Doe"
                                />
                            </FormField>
                            <div className="sm:col-span-2">
                                <FormField
                                    id="email"
                                    label="Email Address"
                                    required
                                    error={errors.email}
                                    hint="Used for notifications and account security"
                                >
                                    <div className="relative">
                                        <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                                        <Input
                                            id="email"
                                            type="email"
                                            className="pl-9"
                                            value={formData.email}
                                            onChange={(e) => handleChange('email', e.target.value)}
                                            onBlur={() => handleBlur('email')}
                                            placeholder="john.doe@company.com"
                                        />
                                    </div>
                                </FormField>
                            </div>
                            <FormField
                                id="location"
                                label="Primary Location"
                                error={errors.location}
                            >
                                <div className="relative">
                                    <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                                    <Input
                                        id="location"
                                        className="pl-9"
                                        value={formData.location}
                                        onChange={(e) => handleChange('location', e.target.value)}
                                        onBlur={() => handleBlur('location')}
                                        placeholder="e.g. Headquarters, London"
                                    />
                                </div>
                            </FormField>
                            <FormField
                                id="phoneNumber"
                                label="Phone Number"
                                error={errors.phoneNumber}
                            >
                                <div className="relative">
                                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground font-medium">+</span>
                                    <Input
                                        id="phoneNumber"
                                        className="pl-7"
                                        value={formData.phoneNumber}
                                        onChange={(e) => handleChange('phoneNumber', e.target.value)}
                                        onBlur={() => handleBlur('phoneNumber')}
                                        placeholder="1 234 567 890"
                                    />
                                </div>
                            </FormField>
                        </div>

                        <DialogFooter>
                            <Button type="button" variant="outline" onClick={() => setEditOpen(false)}>
                                Cancel
                            </Button>
                            <Button type="submit" disabled={saving}>
                                {saving ? (
                                    <>
                                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                        Saving...
                                    </>
                                ) : (
                                    <>
                                        <Check className="h-4 w-4 mr-2" />
                                        Save Changes
                                    </>
                                )}
                            </Button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default UserProfile;
